import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardBody, CardFooter, Input, Typography } from "@material-tailwind/react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm, SubmitHandler } from "react-hook-form";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "hooks/hooks";
import { fetchFieldByid, updateField } from "redux/features/Admin/Fields/fieldSlice";
import { z } from "zod";
import toast from "react-hot-toast";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { PhotoIcon, TrashIcon, XMarkIcon } from "@heroicons/react/24/solid";

// Zod schema for form validation
const fieldSchema = z
  .object({
    designation: z
      .string()
      .min(1, "La désignation est requise")
      .max(100, "La désignation ne doit pas dépasser 100 caractères"),
    oldImages: z.array(z.string()).optional(),
    newImages: z
      .array(z.instanceof(File))
      .optional()
      .refine(
        (files) =>
          !files ||
          files.every(
            (file) =>
              file.size <= 10 * 1024 * 1024 &&
              ["image/jpeg", "image/png", "image/gif", "image/webp"].includes(file.type)
          ),
        "Chaque image doit être inférieure à 10MB et au format JPEG, PNG, GIF ou WebP"
      ),
  })
  .refine(
    (data) => (data.oldImages && data.oldImages.length > 0) || (data.newImages && data.newImages.length > 0),
    {
      message: "Au moins une image est requise",
      path: ["newImages"],
    }
  );

type FieldFormData = z.infer<typeof fieldSchema>;

interface Field {
  designation: string;
  id: number;
  images: string[];
}

// Animation variants
const dialogVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: "easeOut" } },
  exit: { opacity: 0, scale: 0.95, transition: { duration: 0.2, ease: "easeIn" } },
};

const fieldVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

interface UpdateFieldPageProps {
  className?: string;
  fieldId: string;
  onClose: () => void;
}

const UpdateFieldPage: React.FC<UpdateFieldPageProps> = ({ className = "", fieldId, onClose }) => {
  const dispatch = useAppDispatch();
  const fieldState = useAppSelector((state) => state.featureField.field);
  const loadingState = useAppSelector((state) => state.featureField.status);
  const [loading, setLoading] = useState(false);
  const [oldImages, setOldImages] = useState<string[]>([]);
  const [newImages, setNewImages] = useState<File[]>([]);

  const cloudFrontUrl = process.env.REACT_APP_CLOUD_FRONT || "";

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FieldFormData>({
    resolver: zodResolver(fieldSchema),
    defaultValues: {
      designation: "",
      oldImages: [],
      newImages: [],
    },
  });

  useEffect(() => {
    if (fieldId) {
      dispatch(fetchFieldByid(fieldId));
    }
  }, [fieldId, dispatch]);

  useEffect(() => {
    if (fieldState) {
      setValue("designation", fieldState.designation || "");
      setValue("oldImages", fieldState.images || []);
      setOldImages(fieldState.images || []);
      reset({
        designation: fieldState.designation || "",
        oldImages: fieldState.images || [],
        newImages: [],
      });
    }
  }, [fieldState, setValue, reset]);

  useEffect(() => {
    setLoading(loadingState === "loading");
  }, [loadingState]);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      const fileArray = Array.from(files);
      const updatedImages = [...newImages, ...fileArray];
      setNewImages(updatedImages);
      setValue("newImages", updatedImages, { shouldValidate: true });
    }
  };

  const handleDeleteOldImage = (index: number) => {
    const updatedImages = oldImages.filter((_, i) => i !== index);
    setOldImages(updatedImages);
    setValue("oldImages", updatedImages, { shouldValidate: true });
  };

  const handleDeleteNewImage = (index: number) => {
    const updatedImages = newImages.filter((_, i) => i !== index);
    setNewImages(updatedImages);
    setValue("newImages", updatedImages, { shouldValidate: true });
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<FieldFormData> = async (data) => {
    try {
      setLoading(true);
      const response = await dispatch(
        updateField({
          id: fieldId,
          data: {
            designation: data.designation,
            oldImages: data.oldImages || [],
            newImages: data.newImages || [],
          },
        })
      );

      if (response.payload?.status === "OK") {
        toast.success("Type de prestation mis à jour avec succès !");
        handleClose();
      } else {
        throw new Error(response.payload?.error?.[0]?.msg || "Échec de la mise à jour");
      }
    } catch (error: any) {
      toast.error(error.message || "Une erreur est survenue");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`nc-UpdateFieldPage ${className}`} data-nc-id="UpdateFieldPage">
      <AnimatePresence>
        <motion.div variants={dialogVariants} initial="hidden" animate="visible" exit="exit">
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Card className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl">
              <CardBody className="flex flex-col gap-6 p-6 max-h-[60vh] overflow-y-auto bg-white dark:bg-gray-900 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <Typography
                    id="dialog-title"
                    variant="h5"
                    className="w-full p-4 font-bold tracking-tight text-center text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-t-2xl"
                  >
                    Modifier le type de prestation
                  </Typography>
                  <button
                    type="button"
                    onClick={handleClose}
                    className="p-2 text-white rounded-full hover:bg-blue-700"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {/* Designation Field */}
                <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                  <label className="font-semibold text-gray-700 dark:text-gray-200">Désignation</label>
                  <div className="relative mt-2">
                    <Input
                      size="lg"
                      type="text"
                      placeholder="Nom du type de prestation"
                      {...register("designation")}
                      aria-invalid={!!errors.designation}
                      aria-describedby="designation-error"
                      className="text-gray-900 rounded-lg shadow-sm bg-gray-50 dark:bg-gray-800 dark:text-white focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400"
                    />
                  </div>
                  {errors.designation && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Typography
                        id="designation-error"
                        variant="small"
                        color="red"
                        className="mt-1 text-xs text-red-500 dark:text-red-400"
                      >
                        {errors.designation.message}
                      </Typography>
                    </motion.div>
                  )}
                </motion.div>

                {/* Image Management Field */}
                <motion.div variants={fieldVariants} initial="hidden" animate="visible">
                  <label className="font-semibold text-gray-700 dark:text-gray-200">Images</label>
                  <div className="flex justify-center px-6 pt-5 pb-6 mt-2 border-2 border-dashed rounded-lg border-neutral-300 dark:border-neutral-600">
                    <div className="space-y-1 text-center">
                      <PhotoIcon className="w-12 h-12 mx-auto text-neutral-400" />
                      <div className="flex text-sm text-neutral-6000 dark:text-neutral-300">
                        <label
                          htmlFor="fileInput"
                          className="relative font-medium text-blue-600 rounded-md cursor-pointer hover:text-blue-500"
                        >
                          <span>Ajouter des images</span>
                          <input
                            id="fileInput"
                            type="file"
                            multiple
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            className="sr-only"
                            onChange={handleFileUpload}
                          />
                        </label>
                      </div>
                      <p className="text-xs text-neutral-500 dark:text-neutral-400">
                        PNG, JPG, GIF, WebP jusqu'à 10MB
                      </p>
                    </div>
                  </div>

                  {(oldImages.length > 0 || newImages.length > 0) && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {oldImages.map((image, index) => (
                        <div key={`old-${index}`} className="relative">
                          <img
                            src={`${cloudFrontUrl}${image}`}
                            alt={`Existing ${index}`}
                            className="object-cover w-24 h-24 rounded-lg"
                            onError={(e) => {
                              e.currentTarget.src = "/placeholder-image.jpg";
                              console.error(`Failed to load image: ${cloudFrontUrl}${image}`);
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteOldImage(index)}
                            className="absolute p-1 text-white bg-red-500 rounded-full top-1 right-1 hover:bg-red-600"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      {newImages.map((image, index) => (
                        <div key={`new-${index}`} className="relative">
                          <img
                            src={URL.createObjectURL(image)}
                            alt={`New ${index}`}
                            className="object-cover w-24 h-24 rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => handleDeleteNewImage(index)}
                            className="absolute p-1 text-white bg-red-500 rounded-full top-1 right-1 hover:bg-red-600"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {errors.newImages && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                    >
                      <Typography
                        variant="small"
                        color="red"
                        className="mt-1 text-xs text-red-500 dark:text-red-400"
                      >
                        {errors.newImages.message}
                      </Typography>
                    </motion.div>
                  )}
                </motion.div>
              </CardBody>
              <CardFooter className="flex justify-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-b-2xl">
                <ButtonSecondary
                  type="button"
                  onClick={handleClose}
                  className="px-6 py-2 text-gray-900 bg-gray-200 rounded-full dark:bg-gray-700 dark:text-white hover:bg-gray-300 dark:hover:bg-gray-600"
                  disabled={loading}
                >
                  Annuler
                </ButtonSecondary>
                <ButtonSecondary
                  type="submit"
                  className="px-6 py-2 text-white rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-700 hover:from-blue-600 hover:to-indigo-700 dark:hover:from-blue-700 dark:hover:to-indigo-800"
                  disabled={loading}
                  loading={loading}
                >
                  Mettre à jour
                </ButtonSecondary>
              </CardFooter>
            </Card>
          </form>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default UpdateFieldPage;
